import { TGlobalStores } from '@/editor/stores';
import { CommonController2d } from '../CommonController';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { clientPosToWorldPos } from '@/editor/utils';
import { MeasureAbsorbManager } from '@/editor/manager/absorb/measure/measureAbsorbManager';
import { IMeasureRulerInfo, MeasureRulerModel } from '@/editor/entities/2d/measureRulerModel';
import { EEntityType } from '@/editor/types';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getArrowRulerConfig } from './utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { modelManager } from '@/editor/manager/entityManager';

type NonEmptyArray<T> = [T, ...T[]];
type TInsertEntity = { type: EEntityType; id: string } | null;
type IMeasurePointInfo = { point: IPoint2d; entity: TInsertEntity };

export class OperateMeasure extends CommonController2d {
  private readonly mouseButton = { LEFT: 0, MIDDLE: 1, RIGHT: 2 } as const;
  private _measurePoints: NonEmptyArray<IMeasurePointInfo> | null = null;
  private _legalMousePointInfo: IMeasurePointInfo | null = null;
  private _absorbManger: MeasureAbsorbManager;
  private _ruler: MeasureRulerModel;
  private _hoverEntity: TInsertEntity = null;

  constructor(globalStores: TGlobalStores) {
    super(globalStores);
    this._absorbManger = new MeasureAbsorbManager(globalStores);
    this._absorbManger.updateBaseData();
    this._absorbManger.updateFurnitureLineInfos();
    this._ruler = new MeasureRulerModel({ onChange: this._handleRulerChange.bind(this) });
  }

  override init() {
    super.init();
  }

  private _safePushMeasurePoint(point: IMeasurePointInfo) {
    let shadowPoints = Array.from(this._measurePoints ?? []);
    shadowPoints.push(point);
    // 起点和终点为同一个点，无效
    if (shadowPoints.length === 2 && Point2dUtils.equals(shadowPoints[0].point, shadowPoints[1].point)) return;
    if (shadowPoints.length > 2) {
      // 添加了第三个点，重新确定首个测量点
      this._ruler.clear();
      shadowPoints = shadowPoints.slice(-1);
    }

    this._measurePoints = shadowPoints as NonEmptyArray<IMeasurePointInfo>;
    this._updateSelected();
  }

  private _safePopMeasurePoint() {
    if (!this._measurePoints) return undefined;
    const pointInfo = this._measurePoints.pop();
    if (this._measurePoints.length === 0) {
      this._ruler.clear();
      this._measurePoints = null;
    }
    this._updateSelected();

    return pointInfo;
  }

  private _updateSelected() {
    modelManager.selectedHoveredManager.updateMeasureSelected(
      this._measurePoints?.map((value) => value.entity).filter((entity) => !!entity) ?? []
    );
  }

  private _updateAbsorbHover(absorbEntity: TInsertEntity) {
    const excludeEntity = [
      this._hoverEntity?.id,
      this._measurePoints?.[0]?.entity?.id,
      this._measurePoints?.[1]?.entity?.id
    ].filter(Boolean);
    if (absorbEntity && excludeEntity.includes(absorbEntity.id)) {
      return;
    }
    if (absorbEntity?.type === EEntityType.Furniture || absorbEntity?.type === EEntityType.Assembly) {
      this._gs.editorStateStore.setHoverEntity({
        entityId: absorbEntity?.id ?? '',
        type: absorbEntity?.type ?? EEntityType.EMPTY
      });
    } else {
      this._gs.editorStateStore.setHoverEntity({
        entityId: '',
        type: EEntityType.EMPTY
      });
    }
  }

  private _getWorldPositionFromMouseEvent(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);

    return worldPosition;
  }

  private _clickMouseLeftButtonHandler() {
    if (this._legalMousePointInfo) {
      this._absorbManger.setOrthogonalData(this._legalMousePointInfo.point);
      this._safePushMeasurePoint(this._legalMousePointInfo);
      this._legalMousePointInfo = null;
    }
    this._refreshMeasureRuler();
  }

  private _clickMouseRightButtonHandler() {
    const lastMeasurePoint = this._safePopMeasurePoint();
    if (this._measurePoints?.length) {
      this._absorbManger.setOrthogonalData(this._measurePoints[this._measurePoints.length - 1].point);
    } else {
      this._absorbManger.clearOrthogonalData();
    }
    if (!lastMeasurePoint) {
      // 不存在测量点，退出测量模式
      this.dispose();
      this.exit();
    }
  }

  protected override mouseUp = (e: MouseEvent, isDragged: boolean) => {
    if (isDragged) {
      return;
    }
    if (e.button === this.mouseButton.LEFT) {
      this._clickMouseLeftButtonHandler();
    } else if (e.button === this.mouseButton.RIGHT) {
      this._clickMouseRightButtonHandler();
    }
  };

  protected override mouseMove = (e: MouseEvent) => {
    // 吸附
    const point = this._getWorldPositionFromMouseEvent(e);
    const {
      absorbPoint,
      legal,
      absorbEntity = null
    } = this._absorbManger.calculateAbsorbInfoThenRender(point, {
      allowRandomPoint: false,
      showOrthogonalLine: this._measurePoints?.length !== 2
    }) ?? {};
    this._legalMousePointInfo = null;
    if (legal && absorbPoint) {
      this._legalMousePointInfo = { point: absorbPoint, entity: absorbEntity };
    }
    this._updateAbsorbHover(absorbEntity);
    this._refreshMeasureRuler(absorbPoint ?? point);
  };

  // 量尺标注更新
  private _refreshMeasureRuler(currentPoint?: IPoint2d) {
    // 不满足两个点时
    if (!this._measurePoints?.length || (this._measurePoints.length === 1 && !currentPoint)) return;

    const startPoint = this._measurePoints[0].point;
    const endPoint = this._measurePoints?.[1]?.point ?? currentPoint;
    if (!startPoint || !endPoint) return;

    if (!this._measurePoints?.[1]) {
      // 没有确定终点
      this._ruler.updateModel({ start: startPoint, end: endPoint, measuring: !this._legalMousePointInfo });
      return;
    }

    const { arrow, reverse } = getArrowRulerConfig(this._measurePoints[0].entity, this._measurePoints[1].entity);
    this._ruler.updateModel({ start: startPoint, end: endPoint, editable: arrow, arrow: arrow, reverseArrow: reverse });
  }

  // 处理量尺标注输入
  private _handleRulerChange(value: number, ruleInfo: IMeasureRulerInfo) {
    if (this._measurePoints?.length !== 2) return;
    const { point: measureStart, entity: startEntity } = this._measurePoints[0];
    const { point: measureEnd, entity: endEntity } = this._measurePoints[1];
    const { start: startPoint, end: endPoint, reverseArrow: isReverse } = ruleInfo;
    if (!Point2dUtils.equals(measureStart, startPoint) || !Point2dUtils.equals(measureEnd, endPoint)) return;

    const targetFurnitureId = isReverse ? endEntity?.id : startEntity?.id;
    if (!targetFurnitureId) return;

    const distance = isReverse
      ? value - Point2dUtils.distanceTo(startPoint, endPoint)
      : Point2dUtils.distanceTo(startPoint, endPoint) - value;
    this._gs.measureOperation.moveFurniture(targetFurnitureId, startPoint, endPoint, distance);

    this._covertMeasurePoints(distance, !!isReverse);
    this._refreshMeasureRuler();
    this._updateSelected();
    this._absorbManger.updateFurnitureLineInfos();
  }

  // 完成家具移动操作后更新测量点位置
  private _covertMeasurePoints(distance: number, isReverse: boolean) {
    if (this._measurePoints?.length !== 2) return;
    const { point: measureStart } = this._measurePoints[0];
    const { point: measureEnd } = this._measurePoints[1];
    const lineSeg = new LineSeg2d(measureStart, measureEnd);

    if (isReverse) {
      const newPoints = lineSeg.movePointAlongLine(measureEnd, distance);
      this._measurePoints[1].point = newPoints;
    } else {
      const newPoints = lineSeg.movePointAlongLine(measureStart, distance);
      this._measurePoints[0].point = newPoints;
    }
  }

  clear() {
    this._measurePoints = null;
    this._legalMousePointInfo = null;

    this._ruler.clear();
    this._absorbManger.clearOrthogonalData();
    this._absorbManger.clearModel();
    modelManager.selectedHoveredManager.clearMeasureSelected();
  }

  override dispose() {
    this.clear();
    super.dispose();
  }
}
